import Axios, {
  AxiosResponse,
  CreateAxiosDefaults,
  AxiosInstance,
  isAxiosError,
  InternalAxiosRequestConfig
} from 'axios'
import { isEmpty, isError } from 'lodash-es'

import { VITE_DEPLOYMENT_TAG } from '$constants'
import { filterValues } from '$app/utilities'

import { ResponseInterceptor, RequestInterceptor } from './types'

export type Token = string | null

export const toAuthorization = (token: string) => `Bearer ${token}`

export const successResolver = <T extends AxiosResponse>({
  data
}: T): T['data'] => {
  return data
}

export const errorResolver = <T>(error: unknown) => {
  if (isAxiosError<T>(error)) {
    return error.response?.data
  }
}

export const errorMessageResolver = (error: unknown, defaultMessage = '') => {
  if (isAxiosError(error)) {
    return `${error.response?.data?.message || error.response?.statusText || defaultMessage}`
  }

  if (isError(error)) {
    return error.message || defaultMessage
  }

  return defaultMessage
}

export const errorStatusResolver = (error: unknown) => {
  if (isAxiosError(error)) return error.response?.status || error.status
}

export class CommonAPI {
  public api: AxiosInstance

  protected apiResponse: ResponseInterceptor

  protected apiRequest: RequestInterceptor

  protected requestInjectors: VoidFunction[] = []

  protected errorResponseWatchers: VoidFunction[] = []

  constructor(defaults?: CreateAxiosDefaults) {
    this.api = Axios.create({
      ...defaults,
      headers: {
        Accept: 'application/json',
        ...(defaults?.headers as object)
      }
    })

    if (VITE_DEPLOYMENT_TAG) {
      this.api.defaults.headers.common['x-deployment-tag'] = VITE_DEPLOYMENT_TAG
    }

    this.apiRequest = this.api.interceptors.request
    this.apiResponse = this.api.interceptors.response
  }

  /**
   * Filter out falsy values from payload.
   */
  public filterRequestPayload = () => {
    const process = this.apiRequest.use(req => {
      if (req.data) req.data = filterValues(req.data)
      if (req.params) req.params = filterValues(req.params)

      return req
    })

    return () => this.apiRequest.eject(process)
  }

  public injectRequest = (
    callback: (req: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
  ) => {
    const process = this.apiRequest.use(request => callback(request))

    const cleanup = () => this.apiRequest.eject(process)

    this.requestInjectors.push(cleanup)

    return cleanup
  }

  public watchErrorResponse = (callback: (err: unknown) => void) => {
    const process = this.apiResponse.use(
      response => response,
      error => {
        callback(error)

        throw error
      }
    )

    const cleanup = () => this.apiResponse.eject(process)

    this.errorResponseWatchers.push(cleanup)

    return cleanup
  }

  public destroyRequestInjectors = () => {
    this.requestInjectors.forEach(stop => stop())
    this.requestInjectors = []
  }

  public destroyErrorResponseWatchers = () => {
    this.errorResponseWatchers.forEach(stop => stop())
    this.errorResponseWatchers = []
  }

  public updateToken = (token: Token) => {
    if (!token) {
      delete this.api.defaults.headers.common['Authorization']
    } else {
      this.api.defaults.headers.common['Authorization'] = toAuthorization(token)
    }
  }

  public hasToken = () => {
    return !isEmpty(this.api.defaults.headers.common['Authorization'])
  }

  public successResolver = successResolver

  public errorMessageResolver = errorMessageResolver

  public errorStatusResolver = errorStatusResolver
}
